
import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator";
import Draggable from "./utilities/draggable.vue";
import { EventRecord, EventRecordDetails, EventRecordExtraDetails } from "@/store/site-monitor/types";
import { truncateString } from "@/filters";
import { Getter, namespace } from "vuex-class";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldValues } from "@/store/custom-fields/types";
import customFieldsApi from "@/services/api.customField.service";
import CustomDefinedFieldsDisplay from "./custom-fields/CustomDefinedFieldsDisplay.vue";
import { eventRecordMixins } from "@/mixins/EventRecordMixins";

const CustomFields = namespace("customFields");
const getParsedExtraDetails = eventRecordMixins.methods.getParsedExtraDetails;

@Component({
	components: {
		draggable: Draggable,
		"custom-defined-fields-display": CustomDefinedFieldsDisplay,
	},
	filters: { truncateString },
})
export default class EventRecordDetailsPopout extends Vue {
	@Prop({ type: Boolean, default: false }) isModalShown: Boolean;
	@Prop({ type: Object }) eventRecord: EventRecord;
	@Prop({ type: Number, default: 75 }) maxTitleCharacters: Number;
	@Prop({ type: Number, default: 30 }) maxValueCharacters: Number;

	@CustomFields.Action retrieveCustomFields: ({
		tableType,
		live,
	}: {
		tableType: number;
		live: boolean;
	}) => Promise<void>;
	@CustomFields.State alarmCustomFields: CustomFieldDto[];

	private customFieldAlarmTableType = CustomFieldTableTypes.Alarm;
	private customFieldObjectValues: CustomFieldValues[] = [];
	private eventRecordExtraDetailObjects: EventRecordExtraDetails;
	private isLoading: boolean = false;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Watch("eventRecord.EventRecordDetails.objectId")
	private async retrieveCustomFieldValues(): Promise<void> {
		if (this.eventRecord?.EventRecordDetails?.objectId) {
			try {
				this.isLoading = true;
				this.customFieldObjectValues = await customFieldsApi.retrieveCustomFieldsForObject(
					this.eventRecord.EventRecordDetails.objectId,
					CustomFieldTableTypes.Alarm
				);
			} catch {
				console.log(
					"Failed to get Custom Fields for object with objectId: " + this.eventRecord?.EventRecordDetails?.objectId
				);
				this.isLoading = false;
				this.customFieldObjectValues = [];
			}
		} else {
			this.customFieldObjectValues = [];
		}

		this.isLoading = false;
	}

	@Watch("eventRecord.EventRecordDetails.extraDetails")
	private parseExtraDetailsValue(): void {
		this.parseAndSetExtraDetails();
	}

	@Emit("disableDetailsPopout")
	private disableDetailsPopout(): boolean {
		return false;
	}

	public async mounted(): Promise<void> {
		this.parseAndSetExtraDetails();
	}

	private parseAndSetExtraDetails(): void {
		if (!this.eventRecord || !this.eventRecord.EventRecordDetails) {
			return;
		}

		const extraDetails = this.eventRecord.EventRecordDetails.extraDetails;

		if (extraDetails) {
			this.eventRecordExtraDetailObjects = getParsedExtraDetails(extraDetails);
		}
	}

	private get customFieldsEmpty(): string {
		return !this.customFieldObjectValues || this.customFieldObjectValues.length == 0 ? "custom-fields-empty" : "";
	}

	private get getEventRecordDetails(): EventRecordDetails {
		if (this.eventRecord?.EventRecordDetails) {
			return this.eventRecord.EventRecordDetails;
		} else {
			return null;
		}
	}

	private get extraDetailsEmpty(): boolean {
		return !this.eventRecordExtraDetailObjects || (this.eventRecordExtraDetailObjects.data && this.eventRecordExtraDetailObjects.data.length == 0);
	}

	private get isExtraDetailsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor","ExpandableEventDetails","ExtraDetails"]);
	}
}
